import React, {useCallback, useEffect, useRef, useState} from 'react';
import cx from 'classnames';
import {
    Button,
    Flex,
    Popover,
    PriorityIcon,
    Text,
    Title
} from '@priority-software/priority-style-react';
import {isRTL} from '../../../../Localization';
import {getAllRecordsCount} from '../../actions/form';
import {Form} from '../../../../stores/Form/Form';
import {ModernTooltip} from '../../../../components/Common/Tooltip/ModernTooltip';
import texts from '../../texts';
import styles from './styles.module.scss';
import {createDataTestId} from '../../../../utils/dataTestId';

type Props = {
    disablePrev: boolean;
    disableNext: boolean;
    prevBtnTooltipText: string;
    nextBtnTooltipText: string;
    onNextClick: () => void;
    onPrevClick: () => void;
    prevBtnLabel?: string;
    nextBtnLabel?: string;
    current: string | number;
    total: string | number;
    hasMany: boolean;
    form: Form;
};

const tooltipDelay = 1000;

export const Pagination = (props: Props) => {
    const {
        disableNext,
        disablePrev,
        prevBtnTooltipText,
        nextBtnTooltipText,
        prevBtnLabel,
        nextBtnLabel,
        onNextClick,
        onPrevClick,
        current,
        total,
        form,
        hasMany
    } = props;

    const [locked, setLocked] = useState(false);
    const [showExportPopover, setShowExportPopover] = useState(false);
    const anchorRef = useRef();

    useEffect(() => {
        if (hasMany) setShowExportPopover(false);
    }, [hasMany]);

    const handleButtonClick = async (actionFunc: () => void) => {
        setLocked(true);
        await actionFunc();
        setLocked(false);
    };

    const fetchAllRecordsCount = async () => {
        const res = await getAllRecordsCount(form);
        if (res) {
            setShowExportPopover(true);
        }
    };

    const btnClasses = cx(styles.btn, {[styles.lockedBtn]: locked});

    const closeExportPopover = useCallback(() => {
        setShowExportPopover(false);
    }, []);

    return (
        <Flex
            componentClasses={styles.pagination}
            inline
            {...createDataTestId('pagination-section-container', true)}
        >
            <ModernTooltip
                position="top"
                tooltip={isRTL() ? nextBtnTooltipText : prevBtnTooltipText}
                delay={tooltipDelay}
            >
                <Button
                    roundedCorners={false}
                    componentClasses={btnClasses}
                    disabled={disablePrev}
                    onClick={() => handleButtonClick(onPrevClick)}
                    prefixIcon={isRTL() ? PriorityIcon.arrowRight : PriorityIcon.arrowLeft}
                    skin="text"
                >
                    {prevBtnLabel}
                </Button>
            </ModernTooltip>

            <ModernTooltip
                position="top"
                tooltip={isRTL() ? prevBtnTooltipText : nextBtnTooltipText}
                delay={tooltipDelay}
            >
                <Button
                    roundedCorners={false}
                    componentClasses={btnClasses}
                    skin="text"
                    disabled={disableNext}
                    suffixIcon={isRTL() ? PriorityIcon.arrowLeft : PriorityIcon.arrowRight}
                    onClick={() => handleButtonClick(onNextClick)}
                >
                    {nextBtnLabel}
                </Button>
            </ModernTooltip>

            <div
                className={styles.dataLabel}
                {...createDataTestId('pagination-data-section', false)}
            >
                <span className={styles.item}>{current}</span>{' '}
                <span className={styles.item}>{texts.of_delimeter}</span>{' '}
                <span ref={anchorRef}>
                    {!hasMany ? (
                        <span onClick={() => setShowExportPopover(true)}>{total}</span>
                    ) : (
                        <ModernTooltip zIndex={16000} position="top" tooltip={texts.many_tooltip}>
                            <Text
                                tag="div"
                                skin="caption-1"
                                componentClasses={styles.manyLabel}
                                onClick={fetchAllRecordsCount}
                                {...createDataTestId('label-many-pagination', true)}
                            >
                                {texts.many_label}
                            </Text>
                        </ModernTooltip>
                    )}
                </span>
            </div>

            <Popover
                {...createDataTestId('popover-export-excel-container', true)}
                clickable
                closable
                escClosable
                zIndex={10050}
                anchor={anchorRef.current}
                show={showExportPopover}
                onClose={closeExportPopover}
                content={
                    <div className={styles.popoverContent}>
                        <Title
                            skin="h4"
                            textColor="primary-700"
                            {...createDataTestId('popover-export-excel-title', true)}
                        >
                            {texts.export_to_excel_title.replace('{count}', total)}
                        </Title>

                        <Text
                            tag="p"
                            skin="paragraph-2"
                            fontWeight="thin"
                            {...createDataTestId('popover-export-excel-text', true)}
                        >
                            {texts.export_to_excel_text}
                        </Text>

                        <Button
                            size="small"
                            width="fullWidth"
                            skin="primary"
                            componentClasses={styles.popoverBtn}
                            onClick={() => alert('Should export to excel')}
                        >
                            {texts.export_to_excel_label}
                        </Button>
                    </div>
                }
            />
        </Flex>
    );
};
